import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CaretDown, CaretUp, X } from '@phosphor-icons/react';
import BuildLogs from './BuildLogs';
import Spinner from './Spinner';
import { useBuildLog } from '../contexts/BuildLogContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const DOCK_TAIL_LINES = 300;
const MINIMIZED_KEY = 'app_builder.build_dock_minimized';
const HIDDEN_ON = ['/builder', '/downloads'];

// Gradle output can run to thousands of lines and BuildLogs re-renders all of its text on every
// chunk, so the dock only shows the end of the log. The full log stays on the build page.
const tail = (text, lines) => {
    let start = text.length;
    for (let count = 0; count < lines; count += 1) {
        start = text.lastIndexOf('\n', start - 1);
        if (start <= 0) return text;
    }
    return text.slice(start + 1);
};

const readMinimized = () => {
    try {
        const stored = window.sessionStorage.getItem(MINIMIZED_KEY);
        if (stored !== null) return stored === '1';
    } catch { /* storage can be blocked; fall through to the default */ }
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 719px)').matches;
};

/**
 * Floating build log shown on every page except the build page itself and Downloads, where it
 * would sit over the APK/AAB buttons. The socket stays open there; only the panel is hidden.
 */
export default function BuildLogDock() {
    const { language } = useLanguage();
    const t = translations[language];
    const { pathname } = useLocation();
    const { streamId, flavorName, logs, status, error, dismiss } = useBuildLog();
    const [minimized, setMinimized] = useState(readMinimized);
    const visibleLogs = useMemo(() => tail(logs, DOCK_TAIL_LINES), [logs]);

    if (!streamId || HIDDEN_ON.some((prefix) => pathname.startsWith(prefix))) return null;

    const toggle = () => {
        const next = !minimized;
        setMinimized(next);
        try { window.sessionStorage.setItem(MINIMIZED_KEY, next ? '1' : '0'); } catch { /* per-viewer convenience only */ }
    };
    const running = status === 'running';
    const statusLabel = running ? t.running : status === 'success' ? t.buildSucceeded : t.buildFailed;
    const statusChip = running ? 'chip--warn' : status === 'success' ? 'chip--ok' : 'chip--danger';
    const query = flavorName ? `?flavor=${encodeURIComponent(flavorName)}` : '';

    return <>
        <aside className="dock" role="region" aria-label={t.buildLogDockLabel}>
            <div className="dock__bar">
                <span className="dock__title">
                    {running && <Spinner />}
                    <strong>{flavorName || t.buildLogs}</strong>
                    <span className={`chip ${statusChip}`}>{statusLabel}</span>
                </span>
                <span className="dock__actions">
                    <Link className="btn btn--text" to={`/builder/${encodeURIComponent(streamId)}${query}`}>{t.openFullLog}</Link>
                    <button className="btn dock__icon" type="button" onClick={toggle} aria-expanded={!minimized} aria-label={minimized ? t.expandBuildLog : t.minimizeBuildLog}>
                        {minimized ? <CaretUp size={18} aria-hidden="true" /> : <CaretDown size={18} aria-hidden="true" />}
                    </button>
                    {!running && <button className="btn dock__icon" type="button" onClick={dismiss} aria-label={t.closeBuildLog}><X size={18} aria-hidden="true" /></button>}
                </span>
            </div>
            {!minimized && <div className="dock__body">
                <BuildLogs logs={visibleLogs} className="log--compact" />
                {error && <p className="notice notice--error" role="alert">{error}</p>}
                {status === 'success' && <Link className="btn btn--pill" to={`/downloads${flavorName ? `?q=${encodeURIComponent(flavorName)}` : ''}`}>{t.viewDownloads}</Link>}
            </div>}
            <span className="sr-only" role="status">{running ? '' : statusLabel}</span>
        </aside>
        <div className="dock-spacer" aria-hidden="true" />
    </>;
}
